import { SopCheckResult } from './Argyle';

// Income verification wizard workflow.
// Phases align with backend IncomeWizardPhase.
export enum WizardPhase {
  Readiness = 0,
  Streams = 1,
  Overview = 2,
  Complete = 3,
}

export interface WizardWorkflowState {
  id: number;
  screeningId: number;
  currentPhase: WizardPhase;
  streamIndex: number;
  startedByUserId: number;
  startedDate: string;
  lastUpdatedDate: string;
  overrodeReadiness: boolean;
  overrideUser: number | null;
  overrideDate: string | null;
  completedDate: string | null;
}

/** Maps API payload (camelCase or PascalCase) to WizardWorkflowState. */
export function normalizeWizardState(raw: unknown): WizardWorkflowState | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const id = row['id'] ?? row['Id'];
  const screeningId = row['screeningId'] ?? row['ScreeningId'];
  if (id == null || screeningId == null) {
    return null;
  }
  return {
    id: Number(id),
    screeningId: Number(screeningId),
    currentPhase: Number(row['currentPhase'] ?? row['CurrentPhase'] ?? 0) as WizardPhase,
    streamIndex: Number(row['streamIndex'] ?? row['StreamIndex'] ?? 0),
    startedByUserId: Number(row['startedByUserId'] ?? row['StartedByUserId'] ?? 0),
    startedDate: String(row['startedDate'] ?? row['StartedDate'] ?? ''),
    lastUpdatedDate: String(row['lastUpdatedDate'] ?? row['LastUpdatedDate'] ?? ''),
    overrodeReadiness: !!(row['overrodeReadiness'] ?? row['OverrodeReadiness']),
    overrideUser: (row['overrideUser'] ?? row['OverrideUser'] ?? null) as number | null,
    overrideDate: (row['overrideDate'] ?? row['OverrideDate'] ?? null) as string | null,
    completedDate: (row['completedDate'] ?? row['CompletedDate'] ?? null) as string | null,
  };
}

export function isWizardComplete(state: WizardWorkflowState | null | undefined): boolean {
  return !!state?.completedDate || state?.currentPhase === WizardPhase.Complete;
}

export interface WizardStartRequest {
  overrodeReadiness: boolean;
}

// One row from sp_IncomeWizard_CheckReadiness
export interface ReadinessCondition {
  conditionKey: string;
  conditionLabel: string;
  isMet: boolean;
}

// GET /api/incomever/wizard/readiness/{screeningId}
export interface ReadinessResponse {
  isReady: boolean;
  conditions: ReadinessCondition[];
  canOverride: boolean;
}

export enum PayFrequency {
  Weekly = 0,
  BiWeekly = 1,
  SemiMonthly = 2,
  Monthly = 3,
  Annual = 4,
}

//Payload for POST /api/incomever/wizard/draft-source
export interface SaveDraftIncomeSourceRequest {
  draftId?: number | null;
  wizardStateId: number;
  screeningId: number;
  streamType: number;
  subType: number;
  streamIdentifier: string;
  employerName: string | null;
  incomeAmount: number;
  payFrequency: PayFrequency;
  suggestedAmount: number;
  notes: string | null;
  isExcluded: boolean;
  exclusionReasonId?: number | null;
  isAuditEdit?: boolean;
}

//Row returned from draft-source save or GET draft-sources
export interface DraftIncomeSource {
  id: number;
  wizardStateId: number | null;
  screeningId: number;
  inviteId: number | null;
  streamType: number;
  subType: number;
  streamIdentifier: string;
  employerName: string | null;
  incomeAmount: number;
  payFrequency: PayFrequency;
  suggestedAmount: number;
  status: string;
  createdByUserId: number | null;
  createdDate: string;
  reviewStartedDate: string | null;
  reviewCompletedDate: string | null;
  reviewedByUserId: number | null;
  lastModifiedByUserId: number | null;
  lastModifiedDate: string | null;
  notes: string | null;
  isExcluded: boolean;
  exclusionReasonId: number | null;
  exclusionReasonText: string | null;
  detailJson: string | null;
}

// --- Structured detail interfaces (deserialized from detailJson) ---

export interface WorkNumberDetail {
  firstName: string | null;
  lastName: string | null;
  employerName: string | null;
  employmentStatus: string | null;
  hireDate: string | null;
  lastSyncDate: string | null;
  jobTitle: string | null;
  // Equifax TWN master transaction reference (MASTERSRVRTID).
  masterReferenceNumber: string | null;
  projectedAnnualIncome: number | null;
  rateOfPay: number | null;
  payFrequency: string | null;
  payPeriodFrequency: string | null;
  avgHoursWorked: number | null;
  annualCompHistory: WorkNumberAnnualComp[];
  isDataStale: boolean;
  staleReason: string | null;
  isEmploymentInactive: boolean;
  inactiveReason: string | null;
  isNameMismatch: boolean;
  nameMismatchReason: string | null;
  sopChecks: SopCheckResult[];
}

export interface WorkNumberAnnualComp {
  year: string;
  total: number;
}

export interface PayrollDetail {
  employerName: string | null;
  employmentType: string | null;
  employmentStatus: string | null;
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  payCycle: string | null;
  basePay: number | null;
  basePayUnit: string | null;
  hireDate: string | null;
  terminationDate: string | null;
  terminationReason: string | null;
  recentPaystubs: PayrollPaystubEntry[];
  sopChecks: SopCheckResult[];
}

export interface PayrollPaystubEntry {
  payDate: string;
  grossPay: number;
  netPay: number;
}

export interface BanksyncDetail {
  accountName: string | null;
  accountType: string | null;
  institutionName: string | null;
  currentBalance: number | null;
  subStreams: BanksyncSubStreamDetail[];
}

export interface BanksyncSubStreamDetail {
  id: string;
  name: string;
  confidence: number;
  cadenceDays: number | null;
  avgMonthlyNet: number;
  status: string;
  recentDeposits: BanksyncDepositEntry[];
}

export interface BanksyncDepositEntry {
  date: string;
  amount: number;
  category: string | null;
  payee: string | null;
  description: string | null;
}

export interface DocUploadDetail {
  docType: string; // 'w2' | '1099' | 'paystub'
  taxYear: string | null;
  employerName: string | null;
  recipientName: string | null;
  annualAmount: number | null;
  perPeriodGross: number | null;
  payFrequency: string | null;
  documentId: string | null;
  fileName: string | null;
  authenticityScore: number;
  ocrClassification: string | null;
  ocrStatus: string | null;
  authenticityStatus: string | null;
  isInvalidDocument: boolean;
  invalidReason: string | null;
  fraudSignals: DocFraudSignal[];
  overlayUrls: string[];
  paystubs: DocUploadPaystubEntry[];
  sopChecks: SopCheckResult[];
}

export interface DocUploadPaystubEntry {
  documentId: string | null;
  fileName: string | null;
  employeeName: string | null;
  formType: string | null;
  isInvalidDocument: boolean;
  invalidReason: string | null;
  periodStart: string;
  periodEnd: string;
  payDate: string;
  grossPay: number;
  netPay: number;
  authenticityScore: number;
  authenticityStatus: string | null;
  ocrClassification: string | null;
  fraudSignals: DocFraudSignal[];
  overlayUrls: string[];
}

export interface DocFraudSignal {
  code: string;
  confidence: string;
  description: string;
}

/** Parse detailJson into a typed detail object. Returns null if not present. */
export function parseDraftDetail<T>(draft: DraftIncomeSource | null | undefined): T | null {
  if (!draft?.detailJson) return null;
  try {
    const raw = JSON.parse(draft.detailJson);
    return camelCaseKeys(raw) as T;
  } catch {
    return null;
  }
}

/** Recursively converts object keys to camelCase to handle PascalCase from backend. */
function camelCaseKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(camelCaseKeys);
  }
  if (obj && typeof obj === 'object' && obj.constructor === Object) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const camel = key.charAt(0).toLowerCase() + key.slice(1);
      result[camel] = camelCaseKeys(value);
    }
    return result;
  }
  return obj;
}

/** Composite key matching ArgyleIncomeStream.compositeKey. */
export function draftCompositeKey(d: DraftIncomeSource): string {
  return `${d.streamType}|${d.subType}|${d.streamIdentifier}`;
}

export interface StreamDecision {
  action: 'include' | 'exclude';
  draftSource: SaveDraftIncomeSourceRequest;
}
